import type { ClientMembership, MembershipStatus } from '@salonomy/types';
import type { PillTone } from '@salonomy/ui';
import { dateFormat } from '../../lib/locale.js';

/**
 * WS-13 Feature 3b — DISPLAY-only label/tone/benefit helpers for recurring memberships. These map the
 * server's reconciled subscription enums to copy + tones; they NEVER compute money, a renewal date, or a
 * benefit reduction (all of that is server-derived — ADR-0009). The only date formatting here is
 * presentational (the next-renewal / period-end line), never a billed figure.
 */

const MEMBERSHIP_LABEL: Record<MembershipStatus, string> = {
  trialing: 'Trial',
  active: 'Active',
  past_due: 'Payment due',
  canceled: 'Cancelled',
  // 'incomplete' is the default a SYNTHETIC/inert subscription lands in until a webhook advances it — on
  // the fake gateway (staging) the first charge never confirms, so we say so plainly rather than implying
  // a failure or a taken charge.
  incomplete: 'Setting up',
};

const MEMBERSHIP_TONE: Record<MembershipStatus, PillTone> = {
  trialing: 'cobalt',
  active: 'success',
  past_due: 'danger',
  canceled: 'neutral',
  incomplete: 'soft',
};

export function membershipLabel(status: MembershipStatus): string {
  return MEMBERSHIP_LABEL[status] ?? status;
}

export function membershipTone(status: MembershipStatus): PillTone {
  return MEMBERSHIP_TONE[status] ?? 'neutral';
}

/** A membership is LIVE (the benefit applies) while trialing or active and not scheduled to end. */
export function isMembershipLive(m: ClientMembership): boolean {
  return m.status === 'trialing' || m.status === 'active';
}

/**
 * A SYNTHETIC / INERT membership is one the fake gateway created without ever touching Stripe — no
 * connected-account subscription id was minted. We surface "active, no charge taken" honestly rather than
 * implying a real recurring charge is running. This is the staging posture (ADR-0009 / WS-10 inert plane).
 */
export function isSyntheticMembership(m: ClientMembership): boolean {
  return m.stripeSubscriptionId == null;
}

/** A plain editorial date, e.g. "12 June 2026" — for the next-renewal / period-end line. */
export function membershipDate(iso: string | null): string {
  if (!iso) return '—';
  return dateFormat({
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(new Date(iso));
}

/** 1000 → "10% off" (the member benefit is basis points; DISPLAY only — the till applies the real line). */
export function benefitLabel(bps: number | null): string | null {
  if (bps == null || bps <= 0) return null;
  const pct = bps / 100;
  return `${Number.isInteger(pct) ? pct : pct.toFixed(1)}% member discount`;
}
